import { Attribute, useModelService } from 'services/model/model.service';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { Filters } from 'services/data.service';
import { useBreakdownQuery } from 'pages/entities/queryHooks';
import { useDataRequest } from 'hooks/useDataRequest';

import { attributeToToggleTranslationKey } from 'pages/entities/entities';
import { DataEntryRowRecord, useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { useNavigate } from 'react-router-dom';
import { articleLink, productLink, styleLink } from 'pages/entities/entityhelper';
import { createSubtitledHeaderCell } from 'components/table/DataTableUtil';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { tbColors } from 'styles/colors';
import { IntlShape, useIntl } from 'react-intl';
import { percentageCellExporter } from 'components/table/DataTableExportUtil';

interface BreakdownTableProps {
  keyAttribute: Attribute;
  filterDecorator: (filter: Filters) => Filters;
}

type PerformanceRow = DataEntryRowRecord & { share: number };

const useShareColumn = () => {
  const intl = useIntl();
  const { getMeasure } = useModelService();
  const netSlsGmvValExpOD = getMeasure('netSlsGmvValExpOD');
  const columnHelper = createColumnHelper<PerformanceRow>();
  return columnHelper.accessor('share', {
    meta: {
      sortTitleId: 'insights.report.share',
      valueExporter: percentageCellExporter,
      headerExporter: (intl: IntlShape) =>
        `${intl.formatMessage({ id: 'insights.report.share' })} (${netSlsGmvValExpOD.metric.name})`,
    },
    header: () =>
      createSubtitledHeaderCell(intl.formatMessage({ id: 'insights.report.share' }), netSlsGmvValExpOD.metric.name),
    cell: context => <ShareBar width={context.getValue() * 100} color={tbColors.all.darkBlue['600']} />,
  });
};

export function BreakdownTable({ keyAttribute, filterDecorator }: BreakdownTableProps) {
  const { getMeasures, getMeasure } = useModelService();
  const navigate = useNavigate();

  const {
    dataEntryToDataEntryRowRecord,
    getAttributeArrayForLeadAttribute,
    createAttributeColumn,
    createMeasureColumn,
  } = useDataEntriesInTables();
  const measures = getMeasures(['ordQty', 'retQtyRateExpOD', 'netSlsQtyExpOD', 'netSlsGmvValExpOD']);
  const reaNetSlsGmvValOD = getMeasure('netSlsGmvValExpOD');
  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };
  const { data, isError } = useBreakdownQuery(adjustedRequestData, keyAttribute.code);

  const rows: PerformanceRow[] = data
    ? data.sets.rows.map(entry => {
        return {
          ...dataEntryToDataEntryRowRecord(entry),
          share: data.values ? reaNetSlsGmvValOD.getValue(entry.values) / reaNetSlsGmvValOD.getValue(data.values) : 1,
        };
      })
    : [];

  const tableAttributes = getAttributeArrayForLeadAttribute(keyAttribute.code);
  const attributeColumns = tableAttributes.map(attribute => createAttributeColumn(attribute));

  const measureColumns = measures.map(measure => createMeasureColumn(measure));

  const shareColumn = useShareColumn();

  const columns = [...attributeColumns, shareColumn, ...measureColumns] as ColumnDef<DataEntryRowRecord>[];

  const getRowClick = () => {
    switch (keyAttribute.code) {
      case 'styBrandColor':
        return (row: Row<DataEntryRowRecord>) => {
          void navigate(styleLink(row.original.prdNumber ?? '', row.original.styBrandColor ?? ''));
        };
      case 'artEan':
        return (row: Row<DataEntryRowRecord>) => {
          void navigate(articleLink(row.original.artEan ?? ''));
        };
      case 'prdNumber':
        return (row: Row<DataEntryRowRecord>) => {
          void navigate(productLink(row.original.prdNumber ?? ''));
        };
      default:
        return undefined;
    }
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId={`entities.name.singular.${attributeToToggleTranslationKey[keyAttribute.code] ?? keyAttribute.code}`}
        rows={rows}
        columns={columns}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        isError={isError}
        exportName={`performance-lens-${keyAttribute.name}`}
        onRowClick={getRowClick()}
      />
    </DataTableContextProvider>
  );
}
